// src/components/private/SecondaryNavFundingWidget.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DonateMonthlyFundingBar from '../donate/DonateMonthlyFundingBar';
import KoFiModal from '../KoFiModal';
import { fetchDonatePanelPublic } from '../../pages/private/admin/PrivateDocuments/donatePanel/donatePanelApi';
import { computeMonthlyCostAmount } from '../../pages/private/admin/PrivateDocuments/donatePanel/donatePanelCostUtils';
import { useAuth } from '../../authContext';
import useFundingSocket from './funding/useFundingSocket';
import { fundingLastSeen } from './funding/fundingApi';

const BAR_PULSE_MS = 900;

export default function SecondaryNavFundingWidget() {
  const { isLoggedIn } = useAuth();
  const containerRef = useRef(null);
  const barFillRef = useRef(null);
  const pulseTimerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showKoFiModal, setShowKoFiModal] = useState(false);
  const [panel, setPanel] = useState(null);
  const [barPulse, setBarPulse] = useState(false);

  const triggerBarPulse = useCallback(() => {
    setBarPulse(false);
    window.requestAnimationFrame(() => {
      setBarPulse(true);
      clearTimeout(pulseTimerRef.current);
      pulseTimerRef.current = setTimeout(() => {
        setBarPulse(false);
      }, BAR_PULSE_MS);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadPanel = async () => {
      try {
        const data = await fetchDonatePanelPublic();
        if (!cancelled && data?.ok) {
          setPanel(data.data || null);
          const maxSeq = Number(data.fundingWsMaxSeq ?? 0);
          if (isLoggedIn && maxSeq > 0) {
            await fundingLastSeen(maxSeq).catch(() => {});
          }
        }
      } catch (error) {
        console.error('Error fetching donate panel for secondary nav:', error);
        if (!cancelled) setPanel(null);
      }
    };

    loadPanel();
    return () => {
      cancelled = true;
      clearTimeout(pulseTimerRef.current);
    };
  }, [isLoggedIn]);

  const onFundingRefetch = useCallback((nextPanel) => {
    setPanel(nextPanel || null);
  }, []);

  const onFundingStatsUpdate = useCallback(({ currentMonthDonationEur, currentYearDonationEur }) => {
    setPanel((prev) => {
      const prevMonth = prev?.fundingStats?.currentMonthDonationEur ?? 0;
      if (currentMonthDonationEur > prevMonth) {
        triggerBarPulse();
      }

      return {
        ...(prev || {}),
        fundingStats: {
          currentMonthDonationEur,
          currentYearDonationEur,
        },
      };
    });
  }, [triggerBarPulse]);

  useFundingSocket({
    enabled: true,
    isLoggedIn,
    onFundingRefetch,
    onFundingStatsUpdate,
  });

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  const costRows = Array.isArray(panel?.costRows) ? panel.costRows : [];
  const monthlyDonations = panel?.fundingStats?.currentMonthDonationEur ?? 0;
  const yearlyDonations = panel?.fundingStats?.currentYearDonationEur ?? 0;
  const monthlyCost = useMemo(() => computeMonthlyCostAmount(costRows), [costRows]);

  const compactFunding = useMemo(() => {
    if (monthlyCost <= 0) return null;

    const raised = Number.isFinite(monthlyDonations) ? monthlyDonations : 0;
    const fillPercent = Math.min(100, Math.max(0, (raised / monthlyCost) * 100));

    return {
      progressValue: Math.min(raised, monthlyCost),
      max: monthlyCost,
      fillPercent,
      isComplete: raised >= monthlyCost,
    };
  }, [monthlyCost, monthlyDonations]);

  useEffect(() => {
    const fillEl = barFillRef.current;
    if (!fillEl || !compactFunding) return;
    fillEl.style.setProperty('--funding-pct', `${compactFunding.fillPercent}%`);
  }, [compactFunding]);

  if (!compactFunding) return null;

  const toggleOpen = () => setIsOpen((prev) => !prev);

  const handleOpenDetails = () => {
    setIsOpen(false);
    setShowKoFiModal(true);
  };

  const barFillClassName = [
    'secondary-nav-funding__bar-fill',
    compactFunding.isComplete ? 'secondary-nav-funding__bar-fill--complete' : '',
    barPulse ? 'secondary-nav-funding__bar-fill--pulse' : '',
  ].filter(Boolean).join(' ');

  return (
    <div ref={containerRef} className="secondary-nav-funding">
      <button
        type="button"
        className="secondary-nav-funding__trigger"
        onClick={toggleOpen}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={`Funding progress: ${Math.round(compactFunding.fillPercent)} percent`}
        data-resource="public.donation-panel"
        data-action="read"
        data-route="/api/public/donate-panel"
        data-description="Secondary nav funding widget"
      >
        <span className="secondary-nav-funding__label">Funding</span>
        <span
          className={[
            'secondary-nav-funding__bar-track',
            compactFunding.isComplete ? 'secondary-nav-funding__bar-track--complete' : '',
          ].filter(Boolean).join(' ')}
          aria-hidden="true"
        >
          <span ref={barFillRef} className={barFillClassName} />
        </span>
      </button>

      {isOpen ? (
        <div
          className="secondary-nav-funding__tooltip"
          role="dialog"
          aria-label="Funding progress"
        >
          <DonateMonthlyFundingBar
            costRows={costRows}
            currentMonthDonationEur={monthlyDonations}
            currentYearDonationEur={yearlyDonations}
            className="donate-panel-member__funding--popover"
            idPrefix="nav-"
          />
          <div className="secondary-nav-funding__tooltip-footer">
            <button
              type="button"
              className="secondary-nav-funding__details-link"
              onClick={handleOpenDetails}
              data-resource="public.donation-panel"
              data-action="execute"
              data-route="/api/public/donate-panel"
              data-description="Open donation panel details"
            >
              Details
            </button>
          </div>
        </div>
      ) : null}

      <KoFiModal isOpen={showKoFiModal} onClose={() => setShowKoFiModal(false)} />
    </div>
  );
}
